import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Clock, Users, MessageCircle, CalendarDays, Plus, Trash2, Check, Send, BookOpen, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const fmt = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function coachBubbleBg(senderType) {
  if (senderType === "coach") return "zenith-cta text-white";
  if (senderType === "bot") return "bg-surface-container-lowest text-on-surface";
  return "bg-primary-fixed text-primary-on-container";
}

const SENDER_LABEL = { coach: "You", bot: "Assistant", client: "Client", system: "System" };
const CHANNEL_LABEL = { whatsapp: "WhatsApp", web_chat: "Web" };

function Stat({ label, value }) {
  return (
    <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-ambient" data-testid="coach-stat">
      <p className="text-xs uppercase tracking-widest text-outline">{label}</p>
      <p className="font-headline text-3xl text-primary mt-2">{value}</p>
    </div>
  );
}

function Overview() {
  const [bookings, setBookings] = useState([]);
  const [escalations, setEscalations] = useState([]);
  useEffect(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/coach/escalations").then((r) => setEscalations(r.data)).catch(() => {});
  }, []);
  const now = new Date();
  const upcoming = bookings.filter((b) => b.status === "confirmed" && new Date(b.startTime) > now)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat label="Upcoming" value={upcoming.length} />
        <Stat label="Escalations" value={escalations.length} />
        <Stat label="Total sessions" value={bookings.filter((b) => b.status === "confirmed").length} />
      </div>
      <div>
        <h2 className="text-sm uppercase tracking-widest text-outline mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4" strokeWidth={1.5} /> Your schedule</h2>
        {upcoming.length === 0 ? (
          <div className="bg-surface-container-low rounded-3xl p-8 text-center text-on-surface-variant">No upcoming sessions.</div>
        ) : (
          <div className="space-y-3">
            {upcoming.map((b) => (
              <div key={b.booking_id} className="bg-surface-container-lowest rounded-3xl p-5 shadow-ambient flex items-center justify-between" data-testid="coach-schedule-item">
                <div>
                  <p className="font-headline text-lg text-primary">{b.sessionType}</p>
                  <p className="text-sm text-on-surface-variant mt-0.5">{b.clientName}</p>
                </div>
                <p className="text-sm text-on-surface-variant flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {fmt(b.startTime)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Escalations() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");

  const load = useCallback(() => api.get("/coach/escalations").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const open = async (conv) => {
    setActive(conv);
    const r = await api.get(`/coach/conversation/${conv.conversationId}`);
    setMessages(r.data.messages);
  };

  const send = async (channel) => {
    if (!reply.trim()) return;
    await api.post("/coach/reply", { conversationId: active.conversationId, messageText: reply, channel });
    setReply("");
    const r = await api.get(`/coach/conversation/${active.conversationId}`);
    setMessages(r.data.messages);
  };

  const resolve = async () => {
    await api.post("/coach/resolve-escalation", { conversationId: active.conversationId });
    toast.success("Escalation resolved");
    setActive(null);
    load();
  };

  if (active) {
    return (
      <div>
        <button onClick={() => { setActive(null); load(); }} className="text-sm text-on-surface-variant mb-4 hover:text-primary transition-colors duration-300">← Back to queue</button>
        <div className="flex items-center justify-between mb-4">
          <p className="font-headline text-xl text-primary">{active.clientName}</p>
          <button onClick={resolve} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-primary-fixed text-primary-on-container hover:opacity-90 transition-opacity duration-300" data-testid="resolve-button"><Check className="w-4 h-4" strokeWidth={1.5} /> Resolve</button>
        </div>
        <div className="bg-surface-container-low rounded-3xl p-4 max-h-[46vh] overflow-y-auto no-scrollbar mb-4">
          {messages.map((m) => (
            <div key={m.message_id} className={`flex my-1.5 ${m.senderType === "coach" ? "justify-end" : "justify-center"}`} data-testid="chat-message">
              {m.senderType === "system" ? (
                <div className="glass-blush rounded-2xl px-3 py-2 text-xs text-secondary-on-container max-w-[80%] text-center">{m.messageText}</div>
              ) : (
                <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] text-sm ${coachBubbleBg(m.senderType)}`}>
                  <p className="whitespace-pre-wrap">{m.messageText}</p>
                  <span className="block text-[10px] opacity-70 mt-1">{SENDER_LABEL[m.senderType] || m.senderType} · {CHANNEL_LABEL[m.channel] || m.channel}</span>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send("web_chat")} placeholder="Reply to your client…" className="flex-1 bg-surface-container-low rounded-2xl px-4 py-3.5 text-sm outline-none focus:bg-primary-fixed transition-colors duration-500" data-testid="coach-reply-input" />
          <button onClick={() => send("web_chat")} className="w-12 h-12 rounded-2xl zenith-cta text-white flex items-center justify-center shrink-0" data-testid="coach-reply-send"><Send className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-sm uppercase tracking-widest text-outline mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4" strokeWidth={1.5} /> Pending escalations</h2>
      {list.length === 0 ? (
        <div className="bg-surface-container-low rounded-3xl p-8 text-center text-on-surface-variant" data-testid="no-escalations">No escalations. All calm 🌿</div>
      ) : (
        <div className="space-y-3">
          {list.map((c) => (
            <button key={c.conversationId} onClick={() => open(c)} className="w-full text-left bg-surface-container-lowest rounded-3xl p-5 shadow-ambient hover:bg-primary-fixed transition-colors duration-300" data-testid="escalation-item">
              <div className="flex items-center justify-between">
                <p className="font-headline text-lg text-primary">{c.clientName}</p>
                <span className="w-2.5 h-2.5 rounded-full bg-secondary animate-pulse" />
              </div>
              <p className="text-sm text-on-surface-variant mt-1 truncate">{c.lastMessage}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Clients() {
  const [clients, setClients] = useState([]);
  useEffect(() => { api.get("/coach/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="text-sm uppercase tracking-widest text-outline mb-4 flex items-center gap-2"><Users className="w-4 h-4" strokeWidth={1.5} /> Your clients</h2>
      {clients.length === 0 ? (
        <div className="bg-surface-container-low rounded-3xl p-8 text-center text-on-surface-variant">No clients yet.</div>
      ) : (
        <div className="space-y-3">
          {clients.map((c) => (
            <div key={c.user_id} className="bg-surface-container-lowest rounded-3xl p-5 shadow-ambient flex items-center justify-between" data-testid="client-item">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-primary-fixed flex items-center justify-center font-headline text-primary">{c.firstName?.[0]}{c.lastName?.[0]}</span>
                <div>
                  <p className="font-medium text-on-surface">{c.firstName} {c.lastName}</p>
                  <p className="text-xs text-on-surface-variant">{c.email}</p>
                  {c.metadata?.dietaryRestrictions?.length > 0 && (
                    <div className="flex gap-1.5 mt-1">
                      {c.metadata.dietaryRestrictions.map((d) => <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-container text-secondary-on-container">{d}</span>)}
                    </div>
                  )}
                </div>
              </div>
              <span className="text-sm text-on-surface-variant">{c.sessionCount} sessions</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Setup() {
  const [config, setConfig] = useState(null);
  const [docs, setDocs] = useState([]);
  const [newDoc, setNewDoc] = useState({ title: "", content: "" });
  const [newSlot, setNewSlot] = useState({ day: "Monday", start: "09:00", end: "12:00" });

  const loadAll = useCallback(() => {
    api.get("/coach/availability").then((r) => setConfig(r.data && r.data.weeklySchedule ? r.data : { weeklySchedule: [], bufferMinutes: 15, noticePeriodHours: 24, blackoutDates: [], timezone: "America/New_York" })).catch(() => {});
    api.get("/coach/knowledge").then((r) => setDocs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const saveAvailability = async (cfg) => {
    await api.put("/coach/availability", cfg);
    setConfig(cfg);
    toast.success("Availability saved");
  };

  const addSlot = () => {
    const cfg = { ...config };
    let day = cfg.weeklySchedule.find((d) => d.dayOfWeek === newSlot.day);
    if (!day) { day = { dayOfWeek: newSlot.day, slots: [] }; cfg.weeklySchedule.push(day); }
    day.slots.push({ start: newSlot.start, end: newSlot.end });
    saveAvailability(cfg);
  };

  const removeSlot = (dayName, idx) => {
    const cfg = { ...config };
    const day = cfg.weeklySchedule.find((d) => d.dayOfWeek === dayName);
    day.slots.splice(idx, 1);
    if (day.slots.length === 0) cfg.weeklySchedule = cfg.weeklySchedule.filter((d) => d.dayOfWeek !== dayName);
    saveAvailability(cfg);
  };

  const addDoc = async () => {
    if (!newDoc.title.trim() || !newDoc.content.trim()) return;
    await api.post("/coach/knowledge", newDoc);
    setNewDoc({ title: "", content: "" });
    toast.success("Knowledge added — your assistant just got smarter");
    loadAll();
  };

  const delDoc = async (id) => { await api.delete(`/coach/knowledge/${id}`); loadAll(); };

  if (!config) return <div className="text-on-surface-variant">Loading…</div>;

  const inputCls = "bg-surface-container-low rounded-2xl px-4 py-3 text-sm outline-none focus:bg-primary-fixed transition-colors duration-500";

  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-sm uppercase tracking-widest text-outline mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4" strokeWidth={1.5} /> Weekly availability</h2>
        <div className="space-y-3">
          {DAYS.map((day) => {
            const d = config.weeklySchedule.find((x) => x.dayOfWeek === day);
            return (
              <div key={day} className="bg-surface-container-lowest rounded-2xl p-4 shadow-ambient flex items-center gap-3 flex-wrap">
                <span className="w-24 text-sm font-medium text-on-surface">{day}</span>
                {d && d.slots.length > 0 ? d.slots.map((s, i) => (
                  <span key={`${day}-${s.start}-${s.end}`} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-primary-fixed text-primary-on-container" data-testid="availability-slot">
                    {s.start}–{s.end}
                    <button onClick={() => removeSlot(day, i)} className="hover:text-error" data-testid="remove-slot-button"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                  </span>
                )) : <span className="text-sm text-outline">Unavailable</span>}
              </div>
            );
          })}
        </div>
        <div className="mt-4 bg-surface-container-low rounded-2xl p-4 flex flex-wrap items-end gap-3">
          <select value={newSlot.day} onChange={(e) => setNewSlot({ ...newSlot, day: e.target.value })} className={inputCls} data-testid="slot-day-select">
            {DAYS.map((d) => <option key={d}>{d}</option>)}
          </select>
          <input type="time" value={newSlot.start} onChange={(e) => setNewSlot({ ...newSlot, start: e.target.value })} className={inputCls} data-testid="slot-start" />
          <input type="time" value={newSlot.end} onChange={(e) => setNewSlot({ ...newSlot, end: e.target.value })} className={inputCls} data-testid="slot-end" />
          <button onClick={addSlot} className="inline-flex items-center gap-1.5 px-5 py-3 rounded-2xl zenith-cta text-white text-sm font-medium hover:opacity-90 transition-opacity duration-300" data-testid="add-slot-button"><Plus className="w-4 h-4" strokeWidth={1.5} /> Add slot</button>
        </div>
      </div>

      <div>
        <h2 className="text-sm uppercase tracking-widest text-outline mb-4 flex items-center gap-2"><BookOpen className="w-4 h-4" strokeWidth={1.5} /> Assistant knowledge base</h2>
        <div className="space-y-3 mb-4">
          {docs.map((doc) => (
            <div key={doc.doc_id} className="bg-surface-container-lowest rounded-2xl p-5 shadow-ambient" data-testid="knowledge-item">
              <div className="flex items-center justify-between">
                <p className="font-medium text-primary">{doc.title}</p>
                <button onClick={() => delDoc(doc.doc_id)} className="text-outline hover:text-error transition-colors duration-300" data-testid="delete-knowledge-button"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
              <p className="text-sm text-on-surface-variant mt-1">{doc.content}</p>
            </div>
          ))}
        </div>
        <div className="bg-surface-container-low rounded-2xl p-4 space-y-3">
          <input value={newDoc.title} onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} placeholder="Title (e.g. Snack guidelines)" className={`${inputCls} w-full`} data-testid="knowledge-title" />
          <textarea value={newDoc.content} onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })} placeholder="What should the assistant know?" rows={3} className={`${inputCls} w-full resize-none`} data-testid="knowledge-content" />
          <button onClick={addDoc} className="inline-flex items-center gap-1.5 px-5 py-3 rounded-2xl zenith-cta text-white text-sm font-medium hover:opacity-90 transition-opacity duration-300" data-testid="add-knowledge-button"><Plus className="w-4 h-4" strokeWidth={1.5} /> Add knowledge</button>
        </div>
      </div>
    </div>
  );
}

export default function CoachDashboard() {
  const [params] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const titles = { overview: "Coach overview", escalations: "Conversations", clients: "Clients", setup: "Setup" };
  return (
    <AppShell>
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-headline font-light text-primary tracking-tight">{titles[tab]}</h1>
      </div>
      {tab === "overview" && <Overview />}
      {tab === "escalations" && <Escalations />}
      {tab === "clients" && <Clients />}
      {tab === "setup" && <Setup />}
    </AppShell>
  );
}
